import { useDispatch, useSelector } from "react-redux";
import User from "../../../models/User";
import { getErrorMessage } from "../../../utils/errorUtils";
import { isBooleanTrue, isNotNullOrUndefined, isNumberEmpty, isNumberNotEmpty, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../../../utils/objectUtils";
import logger from "../../../utils/logger";
import { jwtDecode } from "jwt-decode";
import { pick } from "lodash";
import AuthUser from "../../../models/AuthUser";
import Address from "../../../models/Address";
import { getAdmins, getReps } from "../users";
import { getAffiliates, getPartners } from "../orchestrator";
import { refreshNotifications, resetNotifications } from "../notifications";
import { refreshEncounters, resetChat } from "../chat";
import { updateShippingAddress } from "../cart";
import { auth } from "../../../firebase";
import { saveFunnelProgress } from "../../../firebase/Leads";
import { recordWeightChange } from "../../../firebase/WeightHistory";
import { recordNewDashboardTutorialStatus } from "../../../firebase/NewDashboardTutorial";

const initialState = {
	currentAuthUser: undefined,
	currentAuthUserId: undefined,

	isRep: false,
	isAdmin: false,
	isSuperAdmin: false,
	isAffiliateUser: false,
	isPartnerUser: false,
	isCoveredEntityUser: false,
	isHelpStaff: false,
	isStaffManager: false,
	isMarketingManager: false,
	isOwner: false,
	isDesigner: false,
	hasMFA: false,
	emailVerified: false,
	phoneVerified: false,

	coveredEntityUsers: [],
	affiliateUsers: [],
	partnerUsers: [],
	profile: null,
	profileUserId: null,
	firstName: null,
	lastName: null,
	height: null,
	weight: null,
	gender: null,
	dateOfBirth: null,
	BMI: 0,
	shippingState: null,

	intercomUserHash: undefined,

	isAuthLoading: false,
	authError: null
};

const authReducer = (state = initialState, action) => {
	const { type, payload } = action;
	switch (type) {
		case 'UPDATE_AUTH':
			return {
				...state,
				...payload
			};
		case 'UPDATE_AUTH_PROFILE':
			return {
				...state,
				profile: payload
			};
		case 'UPDATE_AUTH_PHONEVERIFIED':
			return {
				...state,
				phoneVerified: payload
			};
		case 'UPDATE_AUTH_FIRSTNAME':
			return {
				...state,
				firstName: payload
			};
		case 'UPDATE_AUTH_LASTNAME':
			return {
				...state,
				lastName: payload
			};
		case 'UPDATE_AUTH_WEIGHT':
			return {
				...state,
				weight: payload
			};
		case 'UPDATE_AUTH_HEIGHT':
			return {
				...state,
				height: payload
			};
		case 'UPDATE_AUTH_GENDER':
			return {
				...state,
				gender: payload
			};
		case 'UPDATE_AUTH_DATEOFBIRTH':
			return {
				...state,
				dateOfBirth: payload
			};
		case 'UPDATE_AUTH_BMI':
			return {
				...state,
				BMI: payload
			};
		case 'UPDATE_AUTH_SHIPPINGSTATE':
			return {
				...state,
				shippingState: payload
			};
		case 'RESET_AUTH':
			return {
				...state,
				...initialState
			};
		default:
			return state;
	}
};

export const getProfile = (userId) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const authState = { ...state.authState };

			if (isStringNotEmpty(userId)) {
				const userMatch = await User.getUserProfileById(userId);
				// console.log('userMatch', userMatch);

				let updatedStateValues = {
					profile: userMatch?.profile ?? {},
					profileUserId: userMatch?.profile?.userId ?? null,
					coveredEntityUsers: userMatch?.coveredEntityUsers ?? [],
					affiliateUsers: userMatch?.affiliateUsers ?? [],
					partnerUsers: userMatch?.partnerUsers ?? [],
					firstName: userMatch?.profile?.firstName ?? null,
					lastName: userMatch?.profile?.lastName ?? null
				};

				/**
				 * A DIFFERENT customer's profile replaces these outright, including with an absent value.
				 *
				 * UPDATE_AUTH merges, so a key omitted from the payload keeps the previous account's value -
				 * and these guards only wrote when the INCOMING value was non-empty. That was invisible while
				 * gender defaulted to 'UNKNOWN', because the incoming value was always non-empty; now that it
				 * defaults to null, a rep impersonating a second patient carries the first patient's answers
				 * into the funnel. The gender step then opens pre-selected, and Continue writes one patient's
				 * sex onto another's profile, lead and encounter.
				 *
				 * For the SAME customer the fill-if-empty behavior is kept, so a value the visitor entered in
				 * the funnel is not clobbered by a slower profile read.
				 *
				 * Keyed on the PREVIOUSLY LOADED PROFILE, not on the auth user. `currentAuthUserId` is the
				 * Firebase user, which stays the staff member throughout an impersonation and is empty on
				 * first load - so keying on it would treat a guest's first profile load as a switch and
				 * replace the answers the funnel put in redux before the account existed, which
				 * `onCreateAccount` reads back. Keying on the loaded profile gives the three cases their right
				 * behavior: first load and re-load fill only what is empty, and moving between two
				 * identified customers replaces outright.
				 *
				 * Gated on the incoming profile actually being identified: `getUserProfileById` answers
				 * `{ profile: null }` rather than throwing when the document reads as missing, and treating
				 * that as "a different customer" would clear the signed-in customer's answers and their cart
				 * address.
				 */
				const isDifferentProfile = isStringNotEmpty(userMatch?.profile?.userId)
					&& isStringNotEmpty(authState?.profileUserId)
					&& authState.profileUserId !== userMatch.profile.userId;

				if (isDifferentProfile || (isNumberEmpty(authState?.height) && isNumberNotEmpty(userMatch?.profile?.height))) {
					updatedStateValues.height = userMatch?.profile?.height ?? null;
				}

				if (isDifferentProfile || (isNumberEmpty(authState?.weight) && isNumberNotEmpty(userMatch?.profile?.weight))) {
					updatedStateValues.weight = userMatch?.profile?.weight ?? null;
				}

				if (isDifferentProfile || (isNumberEmpty(authState?.BMI) && isNumberNotEmpty(userMatch?.profile?.bmi))) {
					// 0, not null: BMI's initial state is 0 and the funnel calls BMI.toFixed() unguarded, so a
					// null here crashes the goals step for an impersonated patient with no stored bmi
					updatedStateValues.BMI = userMatch?.profile?.bmi ?? 0;
				}

				if (isDifferentProfile || (isStringEmpty(authState?.gender) && isStringNotEmpty(userMatch?.profile?.gender))) {
					updatedStateValues.gender = userMatch?.profile?.gender ?? null;
				}

				if (isDifferentProfile || (isStringEmpty(authState?.dateOfBirth) && isStringNotEmpty(userMatch?.profile?.dateOfBirth))) {
					updatedStateValues.dateOfBirth = userMatch?.profile?.dateOfBirth ?? null;
				}

				if (isDifferentProfile || (isStringEmpty(authState?.shippingState) && isObjectNotEmpty(userMatch?.profile?.shippingAddress) && isStringNotEmpty(userMatch?.profile?.shippingAddress?.state))) {
					updatedStateValues.shippingState = userMatch?.profile?.shippingAddress?.state ?? null;

					// the cart's address is replaced alongside the state, including with an empty one. leaving
					// the previous customer's address in the cart while clearing the state it was derived from
					// is how an impersonated order ships to the wrong person
					dispatch(updateShippingAddress(isObjectNotEmpty(userMatch?.profile?.shippingAddress) ? userMatch.profile.shippingAddress : new Address(null).toDto()));
				}

				dispatch({ type: 'UPDATE_AUTH', payload: { ...updatedStateValues } });

				dispatch(refreshEncounters(userId));

				// the caller needs to tell a loaded profile apart from a users document that is not
				// there yet - state cannot say, because a missing document is stored as {}.
				return isObjectNotEmpty(userMatch?.profile) ? userMatch.profile : null;
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			// surface the error and let any existing profile stand
			dispatch({
				type: 'UPDATE_AUTH', payload: {
					isAuthLoading: false,
					authError: error
				}
			});
		}

		return null;
	}
};

// firestore's `isSignedIn()` is `request.auth.uid != null && exists(/users/{uid})`, so the
// notifications list rule needs the users document, not just a token. account creation signs the
// auth user in BEFORE that document is written, so attaching on the token change alone is rejected
// every time. the profile read is the client's view of the same predicate, so the listener waits on
// it - bounded, because a user who still has no document after this budget has a real problem that
// retrying will not fix.
const PROFILE_RETRY_DELAYS_MS = [500, 1000, 2000, 4000, 4000];

export const startNotificationsListener = (userId, isVerifiedStaff, pendingProfile) => {
	return async (dispatch) => {
		try {
			if (isStringEmpty(userId)) {
				return;
			}

			let resolvedProfile = await pendingProfile;

			for (const delayMs of PROFILE_RETRY_DELAYS_MS) {
				if (isObjectNotEmpty(resolvedProfile)) {
					break;
				}

				await new Promise((resolve) => { setTimeout(resolve, delayMs); });

				// the whole budget is around eleven seconds, which is long enough for this customer to
				// sign out and the next one to sign in on a shared device. `getProfile` writes the
				// profile it reads into auth state and re-attaches the encounters listener, so
				// continuing here would do both for the wrong customer.
				if (auth.currentUser?.uid !== userId) {
					return;
				}

				resolvedProfile = await dispatch(getProfile(userId));
			}

			if (isObjectEmpty(resolvedProfile)) {
				const message = 'The user document was still unreadable, so the notifications listener was not attached.';
				logger.logErrorEvent('NOTIFICATIONS_LISTENER_SKIPPED', new Error(message), message, true);
				return;
			}

			dispatch(refreshNotifications(userId, isVerifiedStaff));
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

// `userAddress` saves an address-book entry in the same call, so the profile's shipping address and its entry cost one;
// `userAddressEditId` names the entry when that save is an edit
export const updateProfile = (userId, updatedFields, { userAddress = null, userAddressEditId = null } = {}) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const authState = { ...state.authState };
			const { profile } = authState;

			if (isObjectNotEmpty(profile) && isStringNotEmpty(profile?.userId) && profile?.userId === userId) {
				// redux first. the UI reads this record from here, and holding the update back until
				// Firestore acknowledged is what made every funnel step wait on a round trip - 236 call
				// sites across the three thunks, 215 of them awaited, and not one reads a return value, so
				// nothing was ever waiting for the write, only for state the customer could already be shown
				await dispatch({ type: 'UPDATE_AUTH_PROFILE', payload: { ...profile, ...updatedFields } });

				// deliberately not awaited, and deliberately not returned: returning the promise would put
				// every existing `await dispatch(...)` straight back onto the round trip. a failure is
				// logged rather than surfaced. NOT self-healing for a field written once - gender and
				// dateOfBirth are each dispatched exactly once in the funnel, so a failure here leaves them
				// absent for the session with redux still holding the answer
				// the userId, not the token: during impersonation this is the patient being acted on while
				// the signed-in user is the staff member
				saveFunnelProgress({ profileUserId: userId, profileFields: { ...updatedFields }, ...(isObjectNotEmpty(userAddress) ? { userAddress } : {}), ...(isObjectNotEmpty(userAddress) && isStringNotEmpty(userAddressEditId) ? { userAddressEditId } : {}) }).catch((error) => {
					logger.logReduxErrorEvent(error, getErrorMessage(error), true);
				});
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

/**
 * The biometrics go through their own callable rather than `updateProfile`: `weightHistory` and
 * `weightSnapshot` are derived on the server from the stored document, `bmi` and `height` are computed
 * from the same reading, and `savefunnelprogress` refuses all five so a redux copy can never overwrite
 * what the last reading left.
 *
 * Same shape as `updateProfile` - redux first, the write not awaited and not returned. The values the
 * customer entered are applied optimistically and are never taken back from an answer: two quick
 * dispatches are answered in order, and the first answer must not overwrite the second's entered value.
 * Only the two derived fields, which the client cannot compute, are merged when the answer arrives, and
 * only if the same profile is still loaded.
 */
const DERIVED_WEIGHT_FIELDS = ['weightHistory', 'weightSnapshot'];

export const recordProfileWeight = (userId, weight, { bmi = null, height = null, source = null, sourceEntityId = null } = {}) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const authState = { ...state.authState };
			const { profile } = authState;

			if (isObjectNotEmpty(profile) && isStringNotEmpty(profile?.userId) && profile?.userId === userId) {
				const enteredFields = {};
				if (isNotNullOrUndefined(weight)) { enteredFields.weight = weight; }
				if (isNotNullOrUndefined(bmi)) { enteredFields.bmi = bmi; }
				if (isNotNullOrUndefined(height)) { enteredFields.height = height; }
				await dispatch({ type: 'UPDATE_AUTH_PROFILE', payload: { ...profile, ...enteredFields } });

				recordWeightChange({ userId, weight, bmi, height, source, sourceEntityId }).then((result) => {
					const currentProfile = getState()?.authState?.profile;
					const derivedFields = pick(result?.updatedFields ?? {}, DERIVED_WEIGHT_FIELDS);

					if (isObjectNotEmpty(derivedFields) && currentProfile?.userId === userId) {
						dispatch({ type: 'UPDATE_AUTH_PROFILE', payload: { ...currentProfile, ...derivedFields } });
					}
				}).catch((error) => {
					logger.logReduxErrorEvent(error, getErrorMessage(error), true);
				});
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

/**
 * The tutorial outcome goes through its own callable rather than `updateProfile`: the tour is a dashboard
 * concern and `savefunnelprogress` accepts only the four funnel profile fields, so a status sent there is
 * dropped and the patient is shown the tour again on their next visit.
 *
 * Same shape as `updateProfile` - redux first, the write not awaited and not returned. The tour has already
 * closed by the time this runs, so nothing is waiting on the round trip.
 */
export const recordProfileNewDashboardTutorialStatus = (userId, status) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const authState = { ...state.authState };
			const { profile } = authState;

			if (isObjectNotEmpty(profile) && isStringNotEmpty(profile?.userId) && profile?.userId === userId) {
				await dispatch({ type: 'UPDATE_AUTH_PROFILE', payload: { ...profile, newDashboardTutorialStatus: status } });

				recordNewDashboardTutorialStatus({ userId, status }).catch((error) => {
					logger.logReduxErrorEvent(error, getErrorMessage(error), true);
				});
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateAuthFields = (updatedFields) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH', payload: { ...updatedFields } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setProfile = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_PROFILE', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setPhoneVerified = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_PHONEVERIFIED', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setFirstName = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_FIRSTNAME', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setLastName = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_LASTNAME', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setWeight = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_WEIGHT', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setHeight = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_HEIGHT', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setGender = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_GENDER', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setDateOfBirth = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_DATEOFBIRTH', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setBMI = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_BMI', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setShippingState = (val) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_AUTH_SHIPPINGSTATE', payload: val });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const resetAuth = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'RESET_AUTH', payload: { ...initialState } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const autoLogin = () => {
	return async (dispatch) => {
		try {
			console.log('autoLogin');
			dispatch(updateAuthFields({ isAuthLoading: true }));
			const currentUserVal = localStorage.getItem('authUser');
			if (isStringNotEmpty(currentUserVal)) {
				let updatedStateValues = {
					currentAuthUser: null,
					currentAuthUserId: null,
					isRep: false,
					isAdmin: false,
					isSuperAdmin: false,
					isAffiliateUser: false,
					isPartnerUser: false,
					isCoveredEntityUser: false,
					isHelpStaff: false,
					isStaffManager: false,
					isMarketingManager: false,
					isOwner: false,
					isDesigner: false,
					hasMFA: false,
					emailVerified: false,
					phoneVerified: false,
					intercomUserHash: null
				};

				let currentUser = JSON.parse(currentUserVal);
				updatedStateValues.currentAuthUser = currentUser;
				console.log('from localstorage auth: ', currentUser);

				if (isObjectNotEmpty(currentUser) && isStringNotEmpty(currentUser?.uid)) {
					updatedStateValues.emailVerified = isBooleanTrue(currentUser?.emailVerified);
					updatedStateValues.currentAuthUserId = currentUser?.uid;

					if (isStringNotEmpty(currentUser.accessToken)) {
						let accessTokenDecoded = jwtDecode(currentUser.accessToken);
						// console.log('accessTokenDecoded', accessTokenDecoded);
						updatedStateValues.isRep = isBooleanTrue(accessTokenDecoded.isRep);
						updatedStateValues.isAdmin = isBooleanTrue(accessTokenDecoded.isAdmin);
						updatedStateValues.isSuperAdmin = isBooleanTrue(accessTokenDecoded.isSuperAdmin);
						updatedStateValues.isAffiliateUser = isBooleanTrue(accessTokenDecoded.isAffiliateUser);
						updatedStateValues.isPartnerUser = isBooleanTrue(accessTokenDecoded.isPartnerUser);
						updatedStateValues.isCoveredEntityUser = isBooleanTrue(accessTokenDecoded.isCoveredEntityUser);
						updatedStateValues.isHelpStaff = isBooleanTrue(accessTokenDecoded.isHelpStaff);
						updatedStateValues.isStaffManager = isBooleanTrue(accessTokenDecoded.isStaffManager);
						updatedStateValues.isMarketingManager = isBooleanTrue(accessTokenDecoded.isMarketingManager);
						updatedStateValues.isOwner = isBooleanTrue(accessTokenDecoded.isOwner);
						updatedStateValues.isDesigner = isBooleanTrue(accessTokenDecoded.isDesigner);
						updatedStateValues.phoneVerified = isBooleanTrue(accessTokenDecoded.phoneVerified);

						if (isObjectNotEmpty(accessTokenDecoded) && isObjectNotEmpty(accessTokenDecoded?.firebase) && isStringNotEmpty(accessTokenDecoded?.firebase?.sign_in_second_factor) && (accessTokenDecoded?.firebase?.sign_in_second_factor === 'totp' || accessTokenDecoded?.firebase?.sign_in_second_factor === 'phone') && isStringNotEmpty(accessTokenDecoded?.firebase?.second_factor_identifier)) {
							updatedStateValues.hasMFA = true;
						}
					}

					await dispatch({ type: 'UPDATE_AUTH', payload: { ...updatedStateValues } });

					const pendingProfile = dispatch(getProfile(currentUser?.uid));

					dispatch(updateAuthFields({ isAuthLoading: false }));

					if ((isBooleanTrue(updatedStateValues.isRep) || isBooleanTrue(updatedStateValues.isAdmin) || isBooleanTrue(updatedStateValues.isSuperAdmin)) && isBooleanTrue(updatedStateValues.hasMFA) && isBooleanTrue(updatedStateValues.emailVerified)) {
						try {
							dispatch(getAdmins());
							dispatch(getReps());
						} catch (err) {
							logger.logReduxErrorEvent(err, getErrorMessage(err), true);
						}
					}

					if ((isBooleanTrue(updatedStateValues.isRep) || isBooleanTrue(updatedStateValues.isMarketingManager) || isBooleanTrue(updatedStateValues.isAdmin) || isBooleanTrue(updatedStateValues.isSuperAdmin) || isBooleanTrue(updatedStateValues.isAffiliateUser)) && isBooleanTrue(updatedStateValues.hasMFA) && isBooleanTrue(updatedStateValues.emailVerified)) {
						try {
							dispatch(getAffiliates());
						} catch (err) {
							logger.logReduxErrorEvent(err, getErrorMessage(err), true);
						}
					}

					if ((isBooleanTrue(updatedStateValues.isRep) || isBooleanTrue(updatedStateValues.isMarketingManager) || isBooleanTrue(updatedStateValues.isAdmin) || isBooleanTrue(updatedStateValues.isSuperAdmin) || isBooleanTrue(updatedStateValues.isPartnerUser)) && isBooleanTrue(updatedStateValues.hasMFA) && isBooleanTrue(updatedStateValues.emailVerified)) {
						try {
							dispatch(getPartners());
						} catch (err) {
							logger.logReduxErrorEvent(err, getErrorMessage(err), true);
						}
					}

					let isVerifiedStaff = (isBooleanTrue(updatedStateValues.isRep) || isBooleanTrue(updatedStateValues.isMarketingManager) || isBooleanTrue(updatedStateValues.isAdmin) || isBooleanTrue(updatedStateValues.isSuperAdmin)) && isBooleanTrue(updatedStateValues.hasMFA) && isBooleanTrue(updatedStateValues.emailVerified);

					dispatch(startNotificationsListener(currentUser?.uid, isVerifiedStaff, pendingProfile));

					// dispatch(refreshEncounters(currentUser?.uid));

					dispatch(getIntercomUserHash(currentUser.uid));
				}
			}

			dispatch(updateAuthFields({ isAuthLoading: false }));
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			dispatch(resetAuth());
			dispatch(updateAuthFields({ isAuthLoading: false }));

			localStorage.removeItem('authUser');
		}
	}
};

export const getIntercomUserHash = (userId) => {
	return async (dispatch) => {
		try {
			// console.log('getIntercomUserHash');

			try {
				let userHash = await AuthUser.getIntercomUserHash(userId);
				// console.log('intercomUserHash', userHash);
				dispatch({ type: 'UPDATE_AUTH', payload: { intercomUserHash: userHash } });
			} catch (err) {
				logger.logReduxErrorEvent(err, getErrorMessage(err), true);
				dispatch({ type: 'UPDATE_AUTH', payload: { intercomUserHash: null } });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const login = (authUser, force = false) => {
	return async (dispatch, getState) => {
		try {
			console.log('login');
			let updatedStateValues = {
				currentAuthUser: authUser,
				currentAuthUserId: authUser?.uid,
				isRep: false,
				isAdmin: false,
				isSuperAdmin: false,
				isAffiliateUser: false,
				isPartnerUser: false,
				isCoveredEntityUser: false,
				isHelpStaff: false,
				isStaffManager: false,
				isMarketingManager: false,
				isOwner: false,
				isDesigner: false,
				hasMFA: false,
				emailVerified: false,
				phoneVerified: false,
				intercomUserHash: null
			};

			const state = getState();
			const authState = { ...state.authState };
			const { currentAuthUserId } = authState;

			let didAuthChange = false;
			if (isObjectNotEmpty(authUser) && isStringNotEmpty(authUser?.uid)) {
				localStorage.setItem('authUser', JSON.stringify({ ...authUser }));

				updatedStateValues.emailVerified = isBooleanTrue(authUser?.emailVerified);
				updatedStateValues.currentAuthUserId = authUser?.uid;

				if (isStringNotEmpty(authUser.accessToken)) {
					let accessTokenDecoded = jwtDecode(authUser.accessToken);
					// console.log('accessTokenDecoded', accessTokenDecoded);
					updatedStateValues.isRep = isBooleanTrue(accessTokenDecoded.isRep);
					updatedStateValues.isAdmin = isBooleanTrue(accessTokenDecoded.isAdmin);
					updatedStateValues.isSuperAdmin = isBooleanTrue(accessTokenDecoded.isSuperAdmin);
					updatedStateValues.isAffiliateUser = isBooleanTrue(accessTokenDecoded.isAffiliateUser);
					updatedStateValues.isPartnerUser = isBooleanTrue(accessTokenDecoded.isPartnerUser);
					updatedStateValues.isCoveredEntityUser = isBooleanTrue(accessTokenDecoded.isCoveredEntityUser);
					updatedStateValues.isHelpStaff = isBooleanTrue(accessTokenDecoded.isHelpStaff);
					updatedStateValues.isStaffManager = isBooleanTrue(accessTokenDecoded.isStaffManager);
					updatedStateValues.isMarketingManager = isBooleanTrue(accessTokenDecoded.isMarketingManager);
					updatedStateValues.isOwner = isBooleanTrue(accessTokenDecoded.isOwner);
					updatedStateValues.isDesigner = isBooleanTrue(accessTokenDecoded.isDesigner);
					updatedStateValues.phoneVerified = isBooleanTrue(accessTokenDecoded.phoneVerified);

					if (isObjectNotEmpty(accessTokenDecoded) && isObjectNotEmpty(accessTokenDecoded?.firebase) && isStringNotEmpty(accessTokenDecoded?.firebase?.sign_in_second_factor) && (accessTokenDecoded?.firebase?.sign_in_second_factor === 'totp' || accessTokenDecoded?.firebase?.sign_in_second_factor === 'phone') && isStringNotEmpty(accessTokenDecoded?.firebase?.second_factor_identifier)) {
						updatedStateValues.hasMFA = true;
					}
				}

				if (isStringEmpty(currentAuthUserId) || (isStringNotEmpty(authUser?.uid) && currentAuthUserId !== authUser?.uid) || isBooleanTrue(force) ||
					authState?.currentAuthUserId !== updatedStateValues.currentAuthUserId ||
					authState?.isRep !== updatedStateValues?.isRep ||
					authState?.isAdmin !== updatedStateValues?.isAdmin ||
					authState?.isSuperAdmin !== updatedStateValues?.isSuperAdmin ||
					authState?.isAffiliateUser !== updatedStateValues?.isAffiliateUser ||
					authState?.isPartnerUser !== updatedStateValues?.isPartnerUser ||
					authState?.isCoveredEntityUser !== updatedStateValues?.isCoveredEntityUser ||
					authState?.isHelpStaff !== updatedStateValues?.isHelpStaff ||
					authState?.isStaffManager !== updatedStateValues?.isStaffManager ||
					authState?.isMarketingManager !== updatedStateValues?.isMarketingManager ||
					authState?.isOwner !== updatedStateValues?.isOwner ||
					authState?.isDesigner !== updatedStateValues?.isDesigner ||
					authState?.hasMFA !== updatedStateValues?.hasMFA ||
					authState?.emailVerified !== updatedStateValues?.emailVerified ||
					authState?.phoneVerified !== updatedStateValues?.phoneVerified
				) {
					didAuthChange = true;
				}

				if (isBooleanTrue(didAuthChange)) {
					await dispatch({ type: 'UPDATE_AUTH', payload: { ...updatedStateValues } });

					const resolvedProfile = await dispatch(getProfile(authUser?.uid));

					if ((isBooleanTrue(updatedStateValues.isRep) || isBooleanTrue(updatedStateValues.isAdmin) || isBooleanTrue(updatedStateValues.isSuperAdmin)) && isBooleanTrue(updatedStateValues.hasMFA) && isBooleanTrue(updatedStateValues.emailVerified)) {
						try {
							dispatch(getAdmins());
							dispatch(getReps());
						} catch (err) {
							logger.logReduxErrorEvent(err, getErrorMessage(err), true);
						}
					}

					if ((isBooleanTrue(updatedStateValues.isRep) || isBooleanTrue(updatedStateValues.isMarketingManager) || isBooleanTrue(updatedStateValues.isAdmin) || isBooleanTrue(updatedStateValues.isSuperAdmin) || isBooleanTrue(updatedStateValues.isAffiliateUser)) && isBooleanTrue(updatedStateValues.hasMFA) && isBooleanTrue(updatedStateValues.emailVerified)) {
						try {
							dispatch(getAffiliates());
						} catch (err) {
							logger.logReduxErrorEvent(err, getErrorMessage(err), true);
						}
					}

					if ((isBooleanTrue(updatedStateValues.isRep) || isBooleanTrue(updatedStateValues.isMarketingManager) || isBooleanTrue(updatedStateValues.isAdmin) || isBooleanTrue(updatedStateValues.isSuperAdmin) || isBooleanTrue(updatedStateValues.isPartnerUser)) && isBooleanTrue(updatedStateValues.hasMFA) && isBooleanTrue(updatedStateValues.emailVerified)) {
						try {
							dispatch(getPartners());
						} catch (err) {
							logger.logReduxErrorEvent(err, getErrorMessage(err), true);
						}
					}

					let isVerifiedStaff = (isBooleanTrue(updatedStateValues.isRep) || isBooleanTrue(updatedStateValues.isMarketingManager) || isBooleanTrue(updatedStateValues.isAdmin) || isBooleanTrue(updatedStateValues.isSuperAdmin)) && isBooleanTrue(updatedStateValues.hasMFA) && isBooleanTrue(updatedStateValues.emailVerified);

					dispatch(startNotificationsListener(authUser?.uid, isVerifiedStaff, resolvedProfile));

					// dispatch(refreshEncounters(authUser?.uid));

					dispatch(getIntercomUserHash(authUser.uid));
				} else {
					console.log('no refresh needed');
				}
			} else {
				localStorage.removeItem('authUser');

				// this branch is the only sign-out path: `login` is dispatched from one place,
				// `onIdTokenChanged`, and an ordinary token refresh always carries an authUser - so a
				// missing one means the credentials are gone. the listener teardown belongs here and
				// NOT in the catch below, which can run with a live signed-in user.
				dispatch(resetNotifications());
				dispatch(resetChat());

				dispatch(resetAuth());
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			localStorage.removeItem('authUser');
			dispatch(resetAuth());
		}
	}
};

export const useAuth = () => {
	const dispatch = useDispatch();

	return {
		authContext: useSelector((appState) => appState.authState),
		currentAuthUser: useSelector((appState) => appState.authState.currentAuthUser),
		currentAuthUserId: useSelector((appState) => appState.authState.currentAuthUserId),

		isRep: useSelector((appState) => appState.authState.isRep),
		isAdmin: useSelector((appState) => appState.authState.isAdmin),
		isSuperAdmin: useSelector((appState) => appState.authState.isSuperAdmin),
		isAffiliateUser: useSelector((appState) => appState.authState.isAffiliateUser),
		isPartnerUser: useSelector((appState) => appState.authState.isPartnerUser),
		isCoveredEntityUser: useSelector((appState) => appState.authState.isCoveredEntityUser),
		isHelpStaff: useSelector((appState) => appState.authState.isHelpStaff),
		isStaffManager: useSelector((appState) => appState.authState.isStaffManager),
		isMarketingManager: useSelector((appState) => appState.authState.isMarketingManager),
		isOwner: useSelector((appState) => appState.authState.isOwner),
		// a designer authors funnels and is not staff, so it is deliberately absent from isStaff and isVerifiedStaff below
		isDesigner: useSelector((appState) => appState.authState.isDesigner),
		hasMFA: useSelector((appState) => appState.authState.hasMFA),
		emailVerified: useSelector((appState) => appState.authState.emailVerified),
		phoneVerified: useSelector((appState) => appState.authState.phoneVerified),
		isStaff: useSelector((appState) => isBooleanTrue(appState.authState.isRep) || isBooleanTrue(appState.authState.isStaffManager) || isBooleanTrue(appState.authState.isMarketingManager) || isBooleanTrue(appState.authState.isAdmin) || isBooleanTrue(appState.authState.isSuperAdmin)),
		isVerifiedStaff: useSelector((appState) => (isBooleanTrue(appState.authState.isRep) || isBooleanTrue(appState.authState.isStaffManager) || isBooleanTrue(appState.authState.isMarketingManager) || isBooleanTrue(appState.authState.isAdmin) || isBooleanTrue(appState.authState.isSuperAdmin)) && isBooleanTrue(appState.authState.hasMFA) && isBooleanTrue(appState.authState.emailVerified)),

		coveredEntityUsers: useSelector((appState) => appState.authState.coveredEntityUsers),
		affiliateUsers: useSelector((appState) => appState.authState.affiliateUsers),
		partnerUsers: useSelector((appState) => appState.authState.partnerUsers),
		profile: useSelector((appState) => appState.authState.profile),
		profileUserId: useSelector((appState) => appState.authState.profileUserId),

		firstName: useSelector((appState) => appState.authState.firstName),
		lastName: useSelector((appState) => appState.authState.lastName),
		height: useSelector((appState) => appState.authState.height),
		weight: useSelector((appState) => appState.authState.weight),
		gender: useSelector((appState) => appState.authState.gender),
		dateOfBirth: useSelector((appState) => appState.authState.dateOfBirth),
		BMI: useSelector((appState) => appState.authState.BMI),
		shippingState: useSelector((appState) => appState.authState.shippingState),

		intercomUserHash: useSelector((appState) => appState.authState.intercomUserHash),

		isAuthLoading: useSelector((appState) => appState.authState.isAuthLoading),
		authError: useSelector((appState) => appState.authState.authError),

		getProfile: (userId) => dispatch(getProfile(userId)),
		impersonateUser: (userId) => dispatch(getProfile(userId)),
		updateAuthFields: (updatedFields) => dispatch(updateAuthFields(updatedFields)),

		setProfile: (updatedFields) => dispatch(setProfile(updatedFields)),
		setPhoneVerified: (val) => dispatch(setPhoneVerified(val)),
		setFirstName: (val) => dispatch(setFirstName(val)),
		setLastName: (val) => dispatch(setLastName(val)),
		setWeight: (val) => dispatch(setWeight(val)),
		setHeight: (val) => dispatch(setHeight(val)),
		setGender: (val) => dispatch(setGender(val)),
		setDateOfBirth: (val) => dispatch(setDateOfBirth(val)),
		setBMI: (val) => dispatch(setBMI(val)),
		setShippingState: (val) => dispatch(setShippingState(val)),

		resetAuth: () => dispatch(resetAuth()),
		autoLogin: () => dispatch(autoLogin()),
		login: (authUser) => dispatch(login(authUser))
	};
};

export default authReducer;